// Lo PURO de "generar en lote" del agente — ADR-2026-09-08-el-chat-trabaja-en-lote.
//
// Un lote es lo que el agente arma cuando el pedido implica más de una generación
// ("5 imágenes de mis últimos 10 productos", "hacelos en otros colores"): N ítems, cada
// uno con su prompt, su cantidad y sus referencias, que salen en UNA tanda con UNA
// confirmación. Acá vive todo lo que se puede probar sin React ni red: normalizar lo que
// escribió el modelo, los topes, cuántas imágenes se esperan según qué ítems se lanzaron,
// a qué ítem pertenece cada fila que llega, y el resumen que vuelve al agente (que es lo
// que le permite seguir trabajando sobre lo generado).

import { creditosPorResolucion, type ResolucionImagen } from '../../../lib/resolucionImagen';
import { COMO_REUSAR, TOPE_PROMPT_EN_REGISTRO } from '../registroDeGeneracion';

/** `item`: lo que salga de OTRO ítem del mismo lote (una base generada primero). Se resuelve
 *  en el backend, que lanza los niveles en orden
 *  (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend). */
export type FuenteReferencia =
  | 'producto'
  | 'modelo'
  | 'logo'
  | 'adjunto'
  | 'generada'
  | 'item'
  /** Una pose o un fondo GUARDADOS, por id: es lo que permite una serie con una postura
   *  distinta por foto (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id). */
  | 'pose'
  | 'fondo';

export interface ReferenciaLote {
  source: FuenteReferencia;
  product_id?: string;
  model_id?: string;
  image_id?: string;
  /** `source: 'item'`: el id del ítem del que sale la imagen. */
  item_id?: string;
  /** `source: 'pose'`: id de una pose guardada (propia o del catálogo). */
  pose_id?: string;
  /** `source: 'fondo'`: id de un fondo guardado (propio o de la biblioteca). */
  fondo_id?: string;
  /**
   * Sólo con `source: 'adjunto'`: UNA foto puntual del montón que adjuntó el usuario
   * (`f0`, `f1`…). Sin él, "adjunto" sigue significando todas las adjuntas del mensaje —
   * que es lo correcto cuando son pocas y el modelo las vio
   * (ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez).
   */
  foto_id?: string;
  /**
   * Qué es la imagen, dicho por el agente ("Pose: chica caminando", "Producto: remera
   * negra", "Estampa a copiar"): el backend la rotula así y, si empieza con un rol, le
   * pega el bloque de protocolo de ese rol. Con `source: 'adjunto'` sin `foto_id`,
   * `imagen` dice cuál de las adjuntas del mensaje ("Imagen N")
   * (ADR-2026-09-22-el-agente-etiqueta-lo-que-adjunta-el-usuario).
   */
  etiqueta?: string;
  imagen?: number;
  /** Con `source: 'adjunto'`: una imagen subida en un mensaje ANTERIOR, por el id que le
   *  dio el marcador de ese mensaje ("m1-2")
   *  (ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id). */
  adjunto_id?: string;
}

export interface ItemLote {
  /** Estable dentro de la tarjeta (`item-N`): a él se atan el estado y las filas. */
  id: string;
  /** Lo que se muestra: el nombre del producto o lo que puso el agente. */
  etiqueta: string;
  prompt: string;
  cantidad: number;
  referencias: ReferenciaLote[];
  /** El producto principal del ítem (la primera referencia `producto`), para el resumen. */
  productId: string | null;
  /** Lo que el agente declaró del ítem: crear o editar, y si hay mucha piel. Eligen el
   *  motor en el backend (ADR-2026-09-13-el-agente-elige-el-motor-por-la-tarea). */
  tarea?: 'crear' | 'editar' | 'componer' | 'explorar' | 'grafica' | 'cine';
  /** `fusionar` → Seedream primero (ADR-2026-10-07-conservar-o-fusionar). */
  edicion?: 'conservar' | 'fusionar';
  pielExpuesta?: boolean;
  /** Foto de catálogo en fondo blanco puro (ADR-2026-09-13-el-agente-genera-en-fondo-blanco). */
  fondoBlanco?: boolean;
  luzFondoBlanco?: 'difusa' | 'dura';
}

/** Tope de ítems (= requests) por lote: la tanda más grande de Modelo ya manda 18
 *  (6 productos × 3 vistas), y tres de ésas entran en el cap de jobs del backend
 *  (ADR-2026-09-06-tope-de-tres-tandas-en-curso). */
export const MAX_ITEMS_LOTE = 100;
/** Tope de imágenes por lote. Con el worker de a 10 y Gemini en ~17 s por imagen, 100
 *  son ~3 minutos; lo lanza y lo sigue el BACKEND, así que la pestaña puede cerrarse
 *  (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend). */
export const MAX_IMAGENES_LOTE = 100;
/** A partir de cuántos ítems la card se pliega: sólo totales, formato, calidad y el botón. */
export const ITEMS_PARA_PLEGAR = 12;
/** Espejo de `FREEFORM_MAX_CANTIDAD` (no se importa: `agentGenerate` se carga con
 *  import() dinámico y traerlo estático lo metería en el bundle principal). */
export const CANTIDAD_MAX_ITEM = 6;
/** Requests en vuelo a la vez al lanzar. El backend encola igual; esto acota los sockets
 *  abiertos y deja que un 402 corte antes de mandar todo. */
export const CONCURRENCIA_LANZAMIENTO = 4;

const FUENTES: FuenteReferencia[] = [
  'producto', 'modelo', 'logo', 'adjunto', 'generada', 'item', 'pose', 'fondo',
];

const texto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** El id que el agente le puso a un ítem (para que otros lo referencien). Saneado al mismo
 *  alfabeto que valida el backend; vacío si no sirve. */
export const idDeItem = (v: unknown): string => {
  const t = texto(v);
  return /^[A-Za-z0-9_-]{1,40}$/.test(t) ? t : '';
};

/** Las referencias tal como las escribe el modelo → saneadas. Lo que no tiene una fuente
 *  conocida se descarta; una fuente que exige id sin el id, también. */
export function normalizarReferencias(entrada: unknown): ReferenciaLote[] {
  if (!Array.isArray(entrada)) return [];
  const out: ReferenciaLote[] = [];
  for (const r of entrada) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const source = texto(o.source) as FuenteReferencia;
    if (!FUENTES.includes(source)) continue;
    const product_id = texto(o.product_id);
    const model_id = texto(o.model_id);
    const image_id = texto(o.image_id);
    const foto_id = texto(o.foto_id);
    const item_id = idDeItem(o.item_id);
    const pose_id = texto(o.pose_id);
    const fondo_id = texto(o.fondo_id);
    const etiqueta = texto(o.etiqueta).replace(/\s+/g, ' ').slice(0, 120);
    const imagen =
      typeof o.imagen === 'number' && Number.isInteger(o.imagen) && o.imagen >= 1 ? o.imagen : 0;
    const adjunto_id = /^m\d+-\d+$/.test(texto(o.adjunto_id)) ? texto(o.adjunto_id) : '';
    if (source === 'producto' && !product_id) continue;
    if (source === 'modelo' && !model_id) continue;
    if (source === 'generada' && !image_id) continue;
    if (source === 'item' && !item_id) continue;
    // Una fuente por id SIN su id no es una referencia: es ruido que después no resuelve.
    if (source === 'pose' && !pose_id) continue;
    if (source === 'fondo' && !fondo_id) continue;
    out.push({
      source,
      ...(product_id ? { product_id } : {}),
      ...(model_id ? { model_id } : {}),
      ...(image_id ? { image_id } : {}),
      // El `foto_id` sólo tiene sentido en "adjunto"; en otra fuente sería ruido.
      ...(foto_id && source === 'adjunto' ? { foto_id } : {}),
      ...(item_id && source === 'item' ? { item_id } : {}),
      // Se leían y se validaban arriba, pero NO se copiaban acá: el agente mandaba el id y
      // el ítem salía con `{source:"pose"}` pelado, que después nadie resuelve. Dos lotes
      // de 4 salieron con la misma postura por esto
      // (ADR-2026-09-14-el-parser-del-lote-se-comia-el-pose-id).
      ...(pose_id && source === 'pose' ? { pose_id } : {}),
      ...(fondo_id && source === 'fondo' ? { fondo_id } : {}),
      // La etiqueta sirve en las fuentes que NO tienen rótulo propio: adjuntas y generadas
      // (una que sale de otro ítem también es una generada). Producto, modelo, pose, fondo y
      // logo ya llegan rotulados por su tipo.
      ...(etiqueta && (source === 'adjunto' || source === 'generada' || source === 'item')
        ? { etiqueta }
        : {}),
      ...(adjunto_id && source === 'adjunto' && !foto_id ? { adjunto_id } : {}),
      ...(imagen && source === 'adjunto' && !foto_id && !adjunto_id ? { imagen } : {}),
    });
  }
  return out;
}

export const clampCantidadItem = (n: unknown): number => {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.floor(n) : 1;
  return Math.max(1, Math.min(CANTIDAD_MAX_ITEM, v));
};

/**
 * Los ítems tal como llegan en el input de la herramienta (o como los dejó una edición
 * en la tarjeta: el shape es el mismo) → `ItemLote[]`. Un ítem sin prompt no es un ítem.
 * Pasado `MAX_ITEMS_LOTE`, el resto se descarta y se cuenta, para avisar.
 *
 * `nombreDeProducto` resuelve la etiqueta cuando el agente no puso una: el nombre del
 * producto referenciado es lo que el usuario reconoce en la lista.
 */
export function normalizarItemsLote(
  entrada: unknown,
  nombreDeProducto: (productId: string) => string | undefined = () => undefined,
): { items: ItemLote[]; descartados: number } {
  if (!Array.isArray(entrada)) return { items: [], descartados: 0 };
  const items: ItemLote[] = [];
  let descartados = 0;
  for (const raw of entrada) {
    if (!raw || typeof raw !== 'object') continue;
    const o = raw as Record<string, unknown>;
    const prompt = texto(o.prompt);
    if (!prompt) continue;
    if (items.length >= MAX_ITEMS_LOTE) {
      descartados += 1;
      continue;
    }
    const referencias = normalizarReferencias(o.reference_images);
    const productId = referencias.find((r) => r.source === 'producto')?.product_id ?? null;
    const n = items.length + 1;
    const etiqueta =
      texto(o.etiqueta) || (productId ? nombreDeProducto(productId) : undefined) || `Imagen ${n}`;
    // El id lo pone el agente cuando otros ítems lo van a referenciar (`source: "item"`);
    // si no, uno estable por posición. Un id repetido cae al de posición para no pisar.
    const idPedido = idDeItem(o.id);
    const id = idPedido && !items.some((it) => it.id === idPedido) ? idPedido : `item-${n}`;
    items.push({
      id,
      etiqueta,
      prompt,
      cantidad: clampCantidadItem(o.cantidad),
      referencias,
      productId,
      ...(o.tarea === 'crear' || o.tarea === 'editar' || o.tarea === 'componer' || o.tarea === 'explorar' || o.tarea === 'grafica' || o.tarea === 'cine'
        ? { tarea: o.tarea }
        : {}),
      ...(o.edicion === 'conservar' || o.edicion === 'fusionar' ? { edicion: o.edicion } : {}),
      ...(o.piel_expuesta === true ? { pielExpuesta: true } : {}),
      ...(o.fondo_blanco === true ? { fondoBlanco: true } : {}),
      ...(o.luz_fondo_blanco === 'difusa' || o.luz_fondo_blanco === 'dura' ? { luzFondoBlanco: o.luz_fondo_blanco } : {}),
    });
  }
  return { items, descartados };
}

/** El ítem de vuelta al shape del input de la herramienta (para persistir ediciones en
 *  la tarjeta y para que `normalizarItemsLote` lo vuelva a leer igual). */
export function itemAInput(item: ItemLote): Record<string, unknown> {
  return {
    id: item.id,
    etiqueta: item.etiqueta,
    prompt: item.prompt,
    cantidad: item.cantidad,
    reference_images: item.referencias,
    ...(item.tarea ? { tarea: item.tarea } : {}),
    ...(item.edicion ? { edicion: item.edicion } : {}),
    ...(item.pielExpuesta ? { piel_expuesta: true } : {}),
    ...(item.fondoBlanco ? { fondo_blanco: true } : {}),
    ...(item.luzFondoBlanco ? { luz_fondo_blanco: item.luzFondoBlanco } : {}),
  };
}

export const totalImagenes = (items: ItemLote[]): number =>
  items.reduce((acc, it) => acc + it.cantidad, 0);

/**
 * Lo que cuesta el lote: una imagen entregada × la tarifa de la resolución elegida
 * (1K=1 · 2K=2 · 4K=3 — ADR-2026-08-25-resolucion-de-generacion-2k-4k).
 *
 * Hasta ADR-2026-09-13-la-calidad-de-imagen-se-elige-en-la-card el lote salía siempre a
 * 1K y esto era `totalImagenes` a secas. Desde que la card deja elegir la calidad, el
 * `image_size` VIAJA en el pedido y el backend multiplica: sin este factor el botón
 * prometía 5 créditos y se cobraban 15.
 */
export const creditosDelLote = (
  items: ItemLote[],
  resolucion?: ResolucionImagen | null,
): number => totalImagenes(items) * creditosPorResolucion(resolucion);

/** Por qué NO se puede generar este lote, o null si está dentro de los topes. */
export function motivoTopeExcedido(items: ItemLote[]): string | null {
  if (items.length === 0) return 'El lote quedó vacío.';
  if (items.length > MAX_ITEMS_LOTE) {
    return `Un lote puede tener hasta ${MAX_ITEMS_LOTE} ítems; este tiene ${items.length}.`;
  }
  const total = totalImagenes(items);
  if (total > MAX_IMAGENES_LOTE) {
    return `Un lote puede tener hasta ${MAX_IMAGENES_LOTE} imágenes; este pide ${total}. Sacá ítems o bajá cantidades.`;
  }
  const niveles = nivelesDelLote(items);
  if ('error' in niveles) return niveles.error;
  return null;
}

// ── Lanzamiento y filas ───────────────────────────────────────────────────

export type EstadoLanzamiento = 'lanzado' | 'fallo' | 'sin_creditos';

export interface EstadoItem {
  itemId: string;
  executionId: string;
  estado: EstadoLanzamiento;
  error?: string;
}

/** Cuántas imágenes finales se esperan: las de los ítems que SÍ se lanzaron. Es lo que
 *  cierra el poll (un ítem que falló en HTTP nunca escribe filas). */
export function imagenesEsperadas(items: ItemLote[], estados: EstadoItem[]): number {
  const cantidadPorItem = new Map(items.map((it) => [it.id, it.cantidad]));
  return estados
    .filter((e) => e.estado === 'lanzado')
    .reduce((acc, e) => acc + (cantidadPorItem.get(e.itemId) ?? 0), 0);
}

export const ejecucionesLanzadas = (estados: EstadoItem[]): string[] =>
  estados.filter((e) => e.estado === 'lanzado').map((e) => e.executionId);

/** Lo mínimo de una fila de `imagenes_generadas` que el lote necesita. */
export interface FilaDeLote {
  id: string;
  execution_id?: string | null;
  has_error: boolean;
}

/** Filas OK agrupadas por ítem (vía `execution_id`). Una fila sin `execution_id` o de
 *  una ejecución desconocida queda en la clave `''`: se muestra igual, sin dueño. */
export function agruparPorItem(
  filas: FilaDeLote[],
  estados: EstadoItem[],
): Map<string, string[]> {
  const itemPorEjecucion = new Map(estados.map((e) => [e.executionId, e.itemId]));
  const out = new Map<string, string[]>();
  for (const f of filas) {
    if (f.has_error) continue;
    const itemId = (f.execution_id && itemPorEjecucion.get(f.execution_id)) || '';
    const lista = out.get(itemId) ?? [];
    lista.push(f.id);
    out.set(itemId, lista);
  }
  return out;
}

export interface ProgresoLote {
  llegadas: number;
  fallidas: number;
  total: number;
  /** 0–100, sobre `total`. */
  porcentaje: number;
}

export function progresoLote(filas: FilaDeLote[], total: number): ProgresoLote {
  const llegadas = filas.filter((f) => !f.has_error).length;
  const fallidas = filas.length - llegadas;
  const base = Math.max(1, total);
  return {
    llegadas,
    fallidas,
    total,
    porcentaje: Math.min(100, Math.round(((llegadas + fallidas) / base) * 100)),
  };
}

// ── Resumen al agente ─────────────────────────────────────────────────────

export interface ResumenItemLote {
  etiqueta: string;
  product_id: string | null;
  prompt: string;
  image_ids: string[];
  /** Con qué se generó: rehacer pide la misma `tarea` y las mismas referencias, no sólo el
   *  prompt (ADR-2026-10-09-rehacer-no-es-editar). Una referencia a otro ítem va como la
   *  imagen que salió de él (`generada:<id>`). Las cards guardadas antes no lo traen. */
  tarea?: ItemLote['tarea'];
  referencias?: string[];
}

export interface ResumenLote {
  confirmed: boolean;
  image_ids: string[];
  company_id: string;
  total_pedidas: number;
  total_generadas: number;
  por_item: ResumenItemLote[];
  /**
   * `sin_cargo`: se SABE que no consumió créditos — el motor devolvió error (fila con
   * `has_error`, que no cobra), el pedido no salió o no había créditos. Un ítem que se lanzó
   * y no dejó ninguna fila puede seguir generándose y cobrarse al llegar: ése va en `false`
   * (ADR-2026-10-09-el-lote-espera-a-soul-cinema). Las cards guardadas antes no lo traen.
   */
  fallidos: Array<{ etiqueta: string; motivo: string; sin_cargo?: boolean }>;
}

const MOTIVO: Record<EstadoLanzamiento, string> = {
  lanzado: 'no devolvió imágenes',
  fallo: 'el pedido falló',
  sin_creditos: 'sin créditos',
};

/**
 * Lo que vuelve al agente como `tool_result` — y lo que se guarda en la tarjeta. Cada
 * ítem lleva su `product_id` y sus `image_ids`: es lo que permite "agarrá 5 de esos y
 * hacelos en azul" sin volver a preguntar nada.
 */
export function resumenParaAgente(args: {
  items: ItemLote[];
  estados: EstadoItem[];
  filas: FilaDeLote[];
  companyId: string;
}): ResumenLote {
  const porItem = agruparPorItem(args.filas, args.estados);
  const estadoDe = new Map(args.estados.map((e) => [e.itemId, e]));
  // Ejecuciones que dejaron una fila de error: el backend no cobra una generación fallida.
  const conError = new Set(args.filas.filter((f) => f.has_error && f.execution_id).map((f) => f.execution_id));
  const por_item: ResumenItemLote[] = [];
  const fallidos: ResumenLote['fallidos'] = [];
  for (const it of args.items) {
    const ids = porItem.get(it.id) ?? [];
    const estado = estadoDe.get(it.id);
    if (ids.length > 0) {
      por_item.push({
        etiqueta: it.etiqueta,
        product_id: it.productId,
        prompt: it.prompt,
        image_ids: ids,
        ...(it.tarea ? { tarea: it.tarea } : {}),
        ...(it.referencias.length > 0
          ? { referencias: it.referencias.map((r) => referenciaParaRegistro(r, porItem)) }
          : {}),
      });
    } else {
      const motivo = estado?.error || MOTIVO[estado?.estado ?? 'lanzado'];
      const sin_cargo =
        estado?.estado === 'fallo' ||
        estado?.estado === 'sin_creditos' ||
        (!!estado?.executionId && conError.has(estado.executionId));
      fallidos.push({ etiqueta: it.etiqueta, motivo, sin_cargo });
    }
  }
  const sueltas = porItem.get('') ?? [];
  const image_ids = [...por_item.flatMap((p) => p.image_ids), ...sueltas];
  return {
    confirmed: image_ids.length > 0,
    image_ids,
    company_id: args.companyId,
    total_pedidas: totalImagenes(args.items),
    total_generadas: image_ids.length,
    por_item,
    fallidos,
  };
}

/** Una referencia como la escribe el registro de una card suelta (`producto:<id>`). La de
 *  otro ítem se resuelve a la imagen que salió de él: el backend usa la primera. */
function referenciaParaRegistro(r: ReferenciaLote, porItem: Map<string, string[]>): string {
  if (r.source === 'item' && r.item_id) {
    const base = porItem.get(r.item_id)?.[0];
    return base ? `generada:${base}` : `item:${r.item_id}`;
  }
  // Una adjunta va por su id estable del marcador ("m1-2") o del lote de fotos ("f3"); con
  // "Imagen N" del mensaje del lote como única pista, al menos se dice cuál era.
  const id = r.product_id || r.model_id || r.image_id || r.pose_id || r.fondo_id || r.adjunto_id || r.foto_id;
  if (!id && r.source === 'adjunto' && r.imagen) return `adjunto:Imagen ${r.imagen}`;
  return `${r.source}${id ? `:${id}` : ''}`;
}

const recortar = (s: string, n: number): string => {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

/** El `[registro:]` que viaja en el historial de la API en los turnos siguientes: sin
 *  esto el agente olvida qué generó y para qué producto (los tool_use no se re-mandan). */
export function registroLoteParaHistorial(resumen: ResumenLote): string {
  // El prompt de cada ítem va ENTERO (mismo tope que una card suelta): recortado a 120
  // caracteres, "rehaceme la de hora dorada" no tenía de dónde copiarlo y el agente lo
  // reescribía de memoria (ADR-2026-10-09-rehacer-no-es-editar).
  const items = resumen.por_item
    .map(
      (p) =>
        `"${recortar(p.etiqueta, 40)}"${p.product_id ? ` (product_id=${p.product_id})` : ''}: image_ids=${p.image_ids.join(', ')}${p.tarea ? ` — tarea=${p.tarea}` : ''}${p.referencias?.length ? ` — referencias=[${p.referencias.join(', ')}]` : ''} — prompt="${recortar(p.prompt, TOPE_PROMPT_EN_REGISTRO)}"`,
    )
    .join(' · ');
  const fallidos = resumen.fallidos.length
    ? ` Fallaron: ${resumen.fallidos
        .map(
          (f) =>
            `"${recortar(f.etiqueta, 40)}" (${f.motivo}${
              f.sin_cargo === true
                ? '; no consumió créditos'
                : f.sin_cargo === false
                  ? '; no llegó a tiempo y puede seguir generándose: si termina aparece en Creaciones y se cobra, así que no la reintentes sin preguntarle al usuario'
                  : ''
            })`,
        )
        .join(', ')}.`
    : '';
  return `[registro: LOTE generado — ${resumen.total_generadas} de ${resumen.total_pedidas} imágenes OK (usables como source "generada"). Por ítem: ${items || '(ninguno)'}.${fallidos} ${COMO_REUSAR} Para seguir sobre estos productos, armá otro lote con esos product_id.]`;
}

/** Lo que queda en el input de la tarjeta mientras el lote corre — sobrevive recargas
 *  (el chat se persiste) y es lo que el remontaje retoma. Vive acá y no en la tarjeta
 *  porque el diario de lotes (`diarioDeLotes.ts`) guarda exactamente esto. */
export interface PendienteLote {
  company_id: string;
  tanda_id: string;
  started_at: number;
  format: string;
  estados: EstadoItem[];
  expected_count: number;
  /** El job del lote en el backend, si se lanzó por ahí
   *  (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend). */
  lote_job_id?: string;
}

/** Los ítems de los que depende uno: sus referencias `item`. */
export const dependenciasDe = (item: ItemLote): string[] =>
  item.referencias.filter((r) => r.source === 'item' && r.item_id).map((r) => r.item_id as string);

/**
 * Los ítems agrupados en NIVELES por dependencia: el 0 no depende de nadie; cada nivel
 * siguiente sólo de niveles anteriores. Es lo que el backend ejecuta en orden y lo que la
 * card cuenta ("20 base primero, después 80 a partir de esas"). Espejo del mismo cálculo
 * en `services/ads/loteDelAgente.ts` del backend: si cambia uno, cambia el otro.
 */
export function nivelesDelLote(items: readonly ItemLote[]): { niveles: string[][] } | { error: string } {
  const ids = new Set(items.map((it) => it.id));
  const deps = new Map<string, string[]>();
  for (const it of items) {
    const d = dependenciasDe(it);
    for (const dep of d) {
      if (dep === it.id) return { error: `El ítem "${it.etiqueta}" se usa a sí mismo como referencia.` };
      if (!ids.has(dep)) return { error: `El ítem "${it.etiqueta}" referencia a "${dep}", que no está en el lote.` };
    }
    deps.set(it.id, d);
  }
  const listos = new Set<string>();
  const niveles: string[][] = [];
  let pendientes = items.map((it) => it.id);
  while (pendientes.length > 0) {
    const nivel = pendientes.filter((id) => (deps.get(id) ?? []).every((d) => listos.has(d)));
    if (nivel.length === 0) return { error: 'Hay ítems que se referencian en círculo.' };
    niveles.push(nivel);
    for (const id of nivel) listos.add(id);
    pendientes = pendientes.filter((id) => !listos.has(id));
  }
  return { niveles };
}

/** "20 base primero, después 80 a partir de esas" — vacío si es un solo nivel. */
export function descripcionDeNiveles(items: readonly ItemLote[]): string {
  const r = nivelesDelLote(items);
  if ('error' in r || r.niveles.length < 2) return '';
  const cant = new Map(items.map((it) => [it.id, it.cantidad]));
  const imgs = (ids: string[]) => ids.reduce((a, id) => a + (cant.get(id) ?? 0), 0);
  const base = imgs(r.niveles[0]);
  const resto = r.niveles.slice(1).reduce((a, n) => a + imgs(n), 0);
  return `${base} base primero, después ${resto} a partir de esas`;
}

/**
 * Qué dibuja una celda de la grilla del lote — ADR-2026-09-14-el-lote-terminado-no-dice-generando.
 *
 * Existe porque "la celda no tiene URL" significaba DOS cosas distintas y la card dibujaba
 * la misma cosa para las dos:
 *
 *  • `generando` — la imagen todavía no existe. El recuadro azul con la barra es correcto.
 *  • `miniatura` — la imagen YA se generó y sólo falta bajar su URL firmada. Acá el
 *    recuadro azul MIENTE: no hay nada generándose, y con la barra avanzando desde el
 *    montaje el usuario cree que le están cobrando otra tanda. Va un esqueleto neutro,
 *    igual que el de `GenerationResultView` para el mismo caso.
 *
 * El segundo caso es el normal al ENTRAR a un chat viejo: la card se remonta sin miniaturas
 * y las pide de nuevo, así que un lote terminado hace rato mostraba "Generando…" debajo de
 * su propio "Listo. Generamos las N imágenes del lote".
 *
 * Es una función y no un `!src` en el JSX justamente para que el llamador tenga que DECIR
 * cuál de los dos casos es: el bug era que el dato estaba, pero nadie lo preguntaba.
 */
export type EstadoDeCelda = 'imagen' | 'miniatura' | 'generando';

export function estadoDeCelda(src: string | undefined, yaGenerada: boolean): EstadoDeCelda {
  if (src) return 'imagen';
  return yaGenerada ? 'miniatura' : 'generando';
}
